import React from 'react';
import { Link } from 'react-router-dom';
import SectionBackground from '../components/SectionBackground';
import Team from '../components/Team';

const MARCAS_JA_URL = 'https://www.marcasja.com.br/';

const MarcasJaLink = ({ children = 'Marcas Já', className = '' }) => (
  <a
    href={MARCAS_JA_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`text-primary hover:text-primary-hover underline font-medium ${className}`}
  >
    {children}
  </a>
);

const StatCard = ({ value, label, inverted = false, className = '' }) => (
  <div
    className={`rounded-2xl p-6 border shadow-sm text-center h-full ${className} ${
      inverted
        ? 'bg-white/10 border-white/20 backdrop-blur-sm'
        : 'bg-background-white border-gray-100/80'
    }`}
  >
    <div className={`text-3xl md:text-4xl font-bold mb-2 ${inverted ? 'text-primary-light' : 'text-primary'}`}>
      {value}
    </div>
    <div className={`text-sm leading-snug ${inverted ? 'text-gray-300' : 'text-text/70'}`}>{label}</div>
  </div>
);

const PrincipleCard = ({ title, description }) => (
  <div className="bg-background-white rounded-xl p-6 border-l-4 border-primary shadow-sm h-full">
    <h3 className="font-bold text-text mb-2">{title}</h3>
    <p className="text-text/70 text-sm leading-relaxed">{description}</p>
  </div>
);

const DeliverableItem = ({ children }) => (
  <li className="flex items-start gap-3 text-text/80 bg-background-white rounded-lg p-4 border border-gray-100/80">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold mt-0.5">
      ✓
    </span>
    <span className="leading-relaxed text-sm">{children}</span>
  </li>
);

const AboutPage = () => {
  const heroStats = [
    { value: '11+', label: 'Years in the Brazilian IP market' },
    { value: '14,000+', label: 'Trademarks filed at the BPTO' },
    { value: '300+', label: 'Verified five-star Google reviews' },
    { value: '24h', label: 'Filing target after documents and cleared payment' },
  ];

  const standBehindStats = [
    { value: '11+', label: 'Years in the Brazilian IP market' },
    { value: '14,000+', label: 'Trademarks filed at the BPTO' },
    { value: '300+', label: 'Verified five-star Google reviews' },
    { value: 'Thousands', label: 'Active IP assets under monitoring' },
    { value: '6', label: 'Dedicated specialists serving Brazil and the world' },
  ];

  const deliverables = [
    'Priority clearance searches',
    'Filing target within 24 Brazilian business hours after full instructions, documents and cleared payment',
    'Routine procedural docket monitoring and reporting',
    'Office action responses',
    'Opposition filings and defenses',
    'Administrative appeals',
    'Nullity and non-use actions',
    'Renewals, recordals and assignments',
  ];

  const principles = [
    {
      title: 'Trust',
      description:
        'Confidence earned through consistent delivery, clear communication, and outcomes clients can rely on from the first inquiry onward.',
    },
    {
      title: 'Transparency',
      description:
        'Timelines, costs and scope stated honestly. Flat fees upfront, with no success fees, grant fees or hidden costs.',
    },
    {
      title: 'Honesty',
      description:
        'Straight talk on viability and risk. How we operate from the first inquiry through to the final certificate.',
    },
  ];

  return (
    <main className="bg-background min-h-screen">
      <SectionBackground
        image="/images/bg-about.svg"
        overlay="dark"
        minHeight="min-h-[min(520px,70vh)]"
        className="pt-40 md:pt-48 pb-28 md:pb-32 lg:pb-36"
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-14 items-end">
            <div className="xl:col-span-7 text-white">
              <p className="text-primary-light font-semibold text-sm uppercase tracking-wider mb-4">
                About TM RUSH®
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                About Us
              </h1>
              <p className="text-lg text-gray-200 leading-relaxed max-w-2xl mb-8">
                TM RUSH® is a specialized intellectual property firm in Brazil, built as the international arm of{' '}
                <MarcasJaLink className="text-primary-light" />, a Brazilian IP operation founded over a decade ago in
                Porto Alegre. We exist to give international law firms, corporate counsel and foreign applicants a
                reliable, technically rigorous local partner before the Brazilian BPTO (INPI).
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/request-proposal"
                  className="inline-block bg-primary text-white px-8 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md"
                >
                  Start Application
                </Link>
                <a
                  href="#team"
                  className="inline-block bg-white/10 text-white px-8 py-3 rounded-xl hover:bg-white/20 font-semibold border border-white/25 backdrop-blur-sm"
                >
                  Meet the people behind every filing
                </a>
              </div>
            </div>
            <div className="xl:col-span-5 grid grid-cols-2 gap-4">
              {heroStats.map((stat) => (
                <StatCard key={stat.label} inverted value={stat.value} label={stat.label} />
              ))}
            </div>
          </div>
        </div>
      </SectionBackground>

      <section className="bg-background-auxiliary py-16 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">What we stand for</h2>
          <p className="text-text/80 leading-relaxed text-lg max-w-3xl mb-10">
            The firm was built on three principles we don&apos;t negotiate: trust, transparency and honesty. They shape
            how we communicate timelines, costs and outcomes to every client, and how we operate from the first inquiry to
            the final certificate.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {principles.map((p) => (
              <PrincipleCard key={p.title} title={p.title} description={p.description} />
            ))}
          </div>
        </div>
      </section>

      <SectionBackground image="/images/bg-hero.svg" overlay="dark" className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center lg:text-left">
            A foundation built over eleven years
          </h2>
          <div className="space-y-5 text-gray-300 leading-relaxed text-lg">
            <p>
              <MarcasJaLink className="text-primary-light" /> was founded with the conviction that protecting a trademark
              in Brazil shouldn&apos;t be a bureaucratic black box. Our procedural foundation was built under the direct
              mentorship of a former President of the Brazilian BPTO, a rare apprenticeship that shaped the method we
              apply on every docket today.
            </p>
            <p>
              What started as a Brazilian operation grew, over more than eleven years and 14,000+ trademarks filed, into
              one of the highest-rated specialized IP firms in Brazil, verified by 300+ five-star Google reviews. As
              inbound requests from international firms became a recurring need, we launched TM RUSH® as a dedicated
              international arm. Same team, same expertise, same operational discipline, built for the global market.
            </p>
          </div>
        </div>
      </SectionBackground>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="bg-background-auxiliary rounded-2xl p-8 md:p-10 lg:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">What TM RUSH® is (and what it isn&apos;t)</h2>
            <div className="max-w-xl space-y-4 text-text/80 leading-relaxed">
              <p>
                TM RUSH® is a specialized IP firm focused on the administrative lifecycle of trademark prosecution
                before the Brazilian BPTO (INPI). In Brazil, as at the EUIPO, in the UK and in many other
                jurisdictions, administrative IP procedures are handled by qualified IP specialists rather than by
                OAB-registered attorneys, and that is what we are.
              </p>
              <p>
                For judicial enforcement matters (court litigation), which fall outside our administrative scope, we
                work with established partner law firms.
              </p>
            </div>
          </div>
          <div className="bg-background-white rounded-2xl p-8 md:p-10 lg:p-12 border border-gray-100 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">What we deliver</h2>
            <p className="text-text/80 leading-relaxed mb-6">
              For our international partners, we cover the full administrative lifecycle:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {deliverables.map((item) => (
                <DeliverableItem key={item}>{item}</DeliverableItem>
              ))}
            </ul>
            <p className="mt-6 text-sm text-text/60 border-t border-gray-100 pt-4 max-w-xl">
              Services are offered under scope-defined fixed professional fees. Substantive responses and contested proceedings are separately quoted; advance payment applies.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-auxiliary py-16 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-10 text-center">
            The numbers we stand behind
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {standBehindStats.map((stat) => (
              <StatCard key={stat.label} value={stat.value} label={stat.label} />
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">Why this matters</h2>
          <p className="text-text/80 leading-relaxed text-lg">
            Protecting intellectual property in an emerging market shouldn&apos;t feel uncertain. International firms
            deserve a local partner that delivers on time, at the cost stated upfront, with the procedural depth required
            to win at the BPTO. That is what TM RUSH® was built to be, and what we work to be every day.
          </p>
        </div>
      </section>

      <Team />
    </main>
  );
};

export default AboutPage;
